import { useNostr } from "@nostrify/react";
import { useMutation, type UseMutationResult } from "@tanstack/react-query";

import { useCurrentUser } from "./useCurrentUser";

import type { NostrEvent } from "@nostrify/nostrify";

type UnsignedEvent = Omit<NostrEvent, 'id' | 'pubkey' | 'sig' | 'created_at'> & {
  created_at?: number;
};

export function useNostrPublish(): UseMutationResult<NostrEvent, Error, UnsignedEvent> {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();

  return useMutation({
    mutationFn: async (t: UnsignedEvent) => {
      console.log("useNostrPublish: mutationFn called with:", t);

      if (!user) {
        console.error("useNostrPublish: User is not logged in");
        throw new Error("User is not logged in");
      }

      console.log("useNostrPublish: User found:", user.pubkey);
      console.log("useNostrPublish: User signer:", user.signer);
      console.log("useNostrPublish: User method:", user.method);
      console.log("useNostrPublish: window.nostr available:", !!window.nostr);

      const tags = t.tags ?? [];

      // Add the client tag if it doesn't exist
      if (location.protocol === "https:" && !tags.some(([name]) => name === "client")) {
        tags.push(["client", location.hostname]);
      }

      const unsignedEvent = {
        kind: t.kind,
        content: t.content ?? "",
        tags,
        created_at: t.created_at ?? Math.floor(Date.now() / 1000),
      };

      console.log("useNostrPublish: Attempting to sign event:", unsignedEvent);

      // Use window.nostr directly for NIP-07 extension signing
      console.log("useNostrPublish: Checking window.nostr availability...");
      console.log("useNostrPublish: user.method === 'extension':", user.method === 'extension');
      console.log("useNostrPublish: !!window.nostr:", !!window.nostr);

      if (user.method === 'extension' && window.nostr) {
        console.log("useNostrPublish: Using window.nostr.signEvent directly");
        try {
          const event = await window.nostr.signEvent(unsignedEvent);
          console.log("useNostrPublish: Event signed successfully via window.nostr:", event);

          await nostr.event(event, { signal: AbortSignal.timeout(5000) });
          console.log("useNostrPublish: Event published successfully");
          return event;
        } catch (error) {
          console.error("useNostrPublish: Error during signing or publishing via window.nostr:", error);

          // Provide more specific error messages
          if (error instanceof Error) {
            if (error.message.includes("denied") || error.message.includes("rejected")) {
              throw new Error("Signing request was denied. Please approve the request in your Nostr extension.");
            } else if (error.message.includes("timeout")) {
              throw new Error("Signing request timed out. Please check your Nostr extension.");
            }
          }

          throw error;
        }
      }

      // Fallback to user.signer for other login methods
      console.log("useNostrPublish: Calling user.signer.signEvent...");

      try {
        const event = await user.signer.signEvent(unsignedEvent);
        console.log("useNostrPublish: Event signed successfully:", event);

        await nostr.event(event, { signal: AbortSignal.timeout(5000) });
        console.log("useNostrPublish: Event published successfully");
        return event;
      } catch (error) {
        console.error("useNostrPublish: Error during signing or publishing:", error);

        // Provide more specific error messages
        if (error instanceof Error) {
          if (error.message.includes("denied") || error.message.includes("rejected")) {
            throw new Error("Signing request was denied. Please approve the request in your Nostr extension.");
          } else if (error.message.includes("timeout")) {
            throw new Error("Signing request timed out. Please check your Nostr extension.");
          }
        }

        throw error;
      }
    },
    onError: (error) => {
      console.error("Failed to publish event:", error);
    },
    onSuccess: (data) => {
      console.log("Event published successfully:", data);
    },
  });
}